// Bulk Add Teams: upload a CSV/XLSX or paste rows (one team per line), live preview, import via
// POST /api/teams/bulk (players 1-5 main, 6-7 subs), then a per-team / per-player report.
import React, { useMemo, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import AnimatedModal from '../AnimatedModal';
import { IconUploadCloud, IconExternalLink } from '../Icons';
import { parseBulkText, readTeamSheet } from '../../utils/teamImport';

const TEMPLATE = 'https://docs.google.com/spreadsheets/d/147UrAqddnvgqhICLvApFz5aLgp7VOueBR9T_tzGI9c8/';
const TEMPLATE_NAMES = 'https://docs.google.com/spreadsheets/d/11r2vlFgdsGd6i_q9eyGCatEe8GX_zb-BGFd44Qx-aHQ/edit?usp=sharing';

export default function BulkImportModal({ isOpen, onClose, onImported }) {
  const { t } = useTranslation();
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState(null);
  const [note, setNote] = useState(null); // { type: 'error'|'success', message }
  const parsed = useMemo(() => parseBulkText(text), [text]);

  const close = () => { if (busy) return; setText(''); setResults(null); setNote(null); onClose(); };

  const onFile = async (file) => {
    if (!file) return;
    setResults(null); setNote(null);
    try {
      const r = await readTeamSheet(file);
      if (!r.ok) {
        const msg = {
          wrongFileType: t('teamsBulk.wrongFileType', 'Only .csv, .xlsx, or .xls files are accepted.'),
          notPlainText: t('teamsBulk.notPlainText', 'That .csv is not plain text. Re-export it as CSV from your spreadsheet.'),
          numericSteamIds: t('teamsBulk.numericSteamIds', 'Some Steam IDs are stored as numbers in this file, so Excel has already cut off their last digits (cells: {{cells}}). Format the Steam ID columns as Text and re-enter them, use Steam profile links, or export as CSV.', { cells: r.cells }),
          emptyFile: t('teamsBulk.emptyFile', 'No team rows found in that file.'),
        }[r.code] || t('teamsBulk.fileParseFailed', 'Could not read that file. Use a .csv or .xlsx export.');
        setNote({ type: 'error', message: msg });
        return;
      }
      setText(r.lines.join('\n'));
      setNote({ type: 'success', message: t('teamsBulk.fileLoaded', 'Loaded {{count}} rows from file. Review the preview, then import.', { count: r.lines.length }) });
    } catch (_) {
      setNote({ type: 'error', message: t('teamsBulk.fileParseFailed', 'Could not read that file. Use a .csv or .xlsx export.') });
    }
  };

  const submit = async () => {
    if (busy || !parsed.length) return;
    setBusy(true); setNote(null);
    try {
      const res = await axios.post('/api/teams/bulk', { teams: parsed.map(p => ({ name: p.name, players: p.players })) }, { timeout: 600000 });
      setResults(res.data);
      onImported(res.data);
    } catch (err) {
      setNote({ type: 'error', message: err.response?.data?.error || t('teamsBulk.importFailed', 'Bulk import failed') });
    } finally {
      setBusy(false);
    }
  };

  const footer = !results ? (
    <div className="tm-dlg-foot">
      <button type="button" className="tm-btn" onClick={close} disabled={busy}>{t('common.cancel', 'Cancel')}</button>
      <button type="button" className="tm-btn primary" onClick={submit} disabled={busy || !parsed.length}>
        {busy && <span className="tm-spin" />}
        {busy ? t('teamsBulk.importing', 'Importing…') : t('teamsBulk.importCount', 'Import {{count}} teams', { count: parsed.length })}
      </button>
    </div>
  ) : (
    <div className="tm-dlg-foot">
      <button type="button" className="tm-btn primary" onClick={close}>{t('common.done', 'Done')}</button>
    </div>
  );

  return (
    <AnimatedModal isOpen={isOpen} onClose={close} title={t('teamsBulk.title', 'Bulk Add Teams')} maxWidth="760px" footer={footer}>
      {!results ? (
        <div className="tm-dlg">
          <p>{t('teamsBulk.subtitle', 'One team per line. Auto-fetches player names from Steam.')}</p>
          <ol className="tm-steps">
            {[t('teamsBulk.step1', 'Upload a sheet or paste rows'), t('teamsBulk.step2', 'Check the live preview'), t('teamsBulk.step3', 'Click Import')].map((s, i) => (
              <li key={i}><b>{i + 1}</b>{s}</li>
            ))}
          </ol>

          <section className="tm-option">
            <h3>{t('teamsBulk.method1', 'Option 1 - Upload a spreadsheet')}</h3>
            <label className={`tm-drop${busy ? ' busy' : ''}`}>
              <input type="file" className="tm-file" disabled={busy}
                accept=".csv,.xlsx,.xls,text/csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                onChange={e => { const f = e.target.files[0]; onFile(f); e.target.value = ''; }} />
              <IconUploadCloud size={20} />
              <strong>{t('teamsBulk.uploadFile', 'Upload CSV or Excel file')}</strong>
              <span>{t('teamsBulk.uploadFormats', 'Supports .csv, .xlsx, .xls - export straight from Google Sheets')}</span>
            </label>
            <p>{t('teamsBulk.layoutIntro', 'Each row is one team. Column A is the team name, then steam links/IDs. Optionally add a name column after each steam id to lock that player name (empty names auto-fetch from Steam).')}</p>
            <div className="tm-cols-scroll">
              <div className="tm-cols">
                {[['A', t('teamsBulk.colTeam', 'Team Name'), 'team'], ['B', t('teamsBulk.colSteam', 'Steam ID {{n}}', { n: 1 }), 'steam'], ['C', t('teamsBulk.colName', 'Name {{n}}', { n: 1 }) + ' *', ''], ['D', t('teamsBulk.colSteam', 'Steam ID {{n}}', { n: 2 }), 'steam'], ['E', t('teamsBulk.colName', 'Name {{n}}', { n: 2 }) + ' *', ''], ['…', '…', '']].map(([c, l, kind]) => (
                  <div key={c} className={`tm-col ${kind}`}><span>{c}</span><span>{l}</span></div>
                ))}
              </div>
            </div>
            <p className="tm-hint flush">{t('teamsBulk.colNote', '* name columns are optional; 5 main players + 2 optional subs; the header row is skipped automatically')}</p>
            <div className="tm-line">
              <a className="tm-btn small" href={TEMPLATE} target="_blank" rel="noopener noreferrer"><IconExternalLink size={11} /> {t('teamsBulk.exampleSheet', 'View example sheet template')}</a>
              <a className="tm-btn small" href={TEMPLATE_NAMES} target="_blank" rel="noopener noreferrer"><IconExternalLink size={11} /> {t('teamsBulk.exampleSheetNames', 'Example with player names')}</a>
            </div>
          </section>

          <section className="tm-option">
            <h3>{t('teamsBulk.method2', 'Option 2 - Paste rows manually')}</h3>
            <p>{t('teamsBulk.pasteHint', 'One team per line: team name, then players. Use steamid|Name to lock a name. Steam links, Steam64, STEAM_x:y:z all work. Wrap team names with spaces in "quotes".')}</p>
            <textarea className="tm-input mono tall" value={text} onChange={e => setText(e.target.value)} disabled={busy} spellCheck={false}
              aria-label={t('teamsBulk.method2', 'Option 2 - Paste rows manually')}
              placeholder={'Team Saturn 7656...|Niko 7656...|s1mple https://steamcommunity.com/id/player3 7656... 7656...\n"NAVI Junior" 7656... 7656... 7656... 7656... 7656...'} />
            {note && <div className={note.type === 'error' ? 'tm-fail-list' : 'tm-hint flush ok'}>{note.message}</div>}
          </section>

          {parsed.length > 0 && (
            <section className="tm-option">
              <h3>{t('teamsBulk.preview', 'Preview')} <span className="tm-section-count">{parsed.length}</span></h3>
              <div className="tm-preview">
                {parsed.map(p => {
                  const total = p.players.length;
                  const named = p.players.filter(pl => pl.name).length;
                  return (
                    <div key={p.line} className="tm-prev-row">
                      <span className="no">{p.line}</span>
                      <span className={`name${p.name ? '' : ' missing'}`}>{p.name || t('teamsBulk.noName', '(missing team name)')}</span>
                      <span className="nums">
                        {named > 0 && <span className="named" title={t('teamsBulk.namesLocked', '{{count}} player names will be locked', { count: named })}>{t('teamsBulk.namedCount', '{{count}} named', { count: named })}</span>}
                        <span className={total ? 'full' : 'short'}>{Math.min(total, 5)}/5</span>
                        {total > 5 && <span className="subs">+{total - 5} {t('teamsBulk.subs', 'sub')}</span>}
                        {total > 7 && <span className="over" title={t('teamsBulk.tooMany', 'More than 7 players - extras will still be added')}>!</span>}
                      </span>
                    </div>
                  );
                })}
              </div>
            </section>
          )}
        </div>
      ) : (
        <div className="tm-dlg">
          <p className="tm-result-sum">
            {t('teamsBulk.done', 'Created {{teams}} teams and {{players}} players.', { teams: results.created_teams, players: results.created_players })}
          </p>
          <div className="tm-results">
            {results.results.map((r, i) => (
              <div key={i} className={`tm-result${r.ok ? '' : ' bad'}`}>
                <div className="tm-result-head">
                  <strong>{r.name || t('teamsBulk.noNameShort', '(no name)')}</strong>
                  <span className={r.ok ? 'ok' : 'bad'}>{r.ok ? t('teamsBulk.teamCreated', 'created') : r.error}</span>
                </div>
                {(r.players || []).map((pl, j) => (
                  <div key={j} className="tm-result-player">
                    <span className={pl.ok ? 'ok' : 'bad'}>{pl.ok ? t('teamsBulk.ok', 'OK') : t('teamsBulk.failed', 'Failed')}</span>
                    <span className="who">{pl.name || pl.steam_id || pl.input}</span>
                    {pl.ok && pl.role === 'sub' && <span className="tm-sub-word">{t('teams.sub', 'Sub')}</span>}
                    <span className={`detail${pl.ok ? '' : ' bad'}`}>{pl.ok ? pl.steam_id : `${pl.code === 'name_not_fetched' ? t('teams.errNameNotFetched', "Couldn't fetch the player's name from Steam. Enter the name manually.") : pl.error} - ${pl.input}`}</span>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      )}
    </AnimatedModal>
  );
}
